import { EHANDOVER_TAB_IDS } from './config';

// 去掉 tab title 尾部的数量文案。
// MY_DRAFT_TAB 暂时不显示 total，所以需要在渲染标题时把 "(0)" 这类计数去掉。
export const getTabTitleWithoutCount = (title = '') => {
  const trimmedTitle = title.trimEnd();
  if (!trimmedTitle.endsWith(')')) {
    return title;
  }

  const openBracketIndex = trimmedTitle.lastIndexOf('(');
  if (openBracketIndex === -1) {
    return title;
  }

  return trimmedTitle.slice(0, openBracketIndex).trimEnd();
};

// 从统一的 tabCountsById 里读取某个 tab 当前应展示的 total。
// 这里故意兜底成 0，避免首屏数据还没回来时 title 出现 undefined。
const getTabCount = (tabCountsById = {}, tabId) => tabCountsById?.[tabId] ?? 0;

// 生成 EHandover 页面初始 tab 配置。
// 这里只负责“页面初次渲染时 tab 长什么样”，不负责后续 total 联动和 title 动态更新。
export const createInitialTabData = ({ i18n, tabCountsById }) => [
  {
    id: EHANDOVER_TAB_IDS.PIN_TAB,
    flag: false,
    title: i18n.t('EHANDOVER_PIN_TAB', { count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.PIN_TAB) }),
    component: 'pinned',
    isShow: false,
  },
  {
    id: EHANDOVER_TAB_IDS.SENT_TAB,
    flag: true,
    title: i18n.t('EHANDOVER_ALL', { count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.SENT_TAB) }),
    component: 'task',
    isShow: true,
  },
  {
    id: EHANDOVER_TAB_IDS.CHECKLIST_TAB,
    flag: false,
    title: i18n.t('EHANDOVER_FOLLOWUP', { count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.CHECKLIST_TAB) }),
    component: 'followUp',
    isShow: true,
  },
  {
    id: EHANDOVER_TAB_IDS.OUTSTANDING_TAB,
    flag: false,
    title: i18n.t('EHANDOVER_OUTSTANDING_CHECKLIST_TAB', {
      count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.OUTSTANDING_TAB),
    }),
    component: 'outstanding',
    isShow: false,
  },
  {
    id: EHANDOVER_TAB_IDS.COMPLETED_TAB,
    flag: false,
    title: i18n.t('EHANDOVER_COMPLETED_CHECKLIST_TAB', {
      count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.COMPLETED_TAB),
    }),
    component: 'complete',
    isShow: false,
  },
  {
    id: EHANDOVER_TAB_IDS.SCHEDULE_TAB,
    flag: false,
    title: i18n.t('EHANDOVER_TAB_POSTDAY', { count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.SCHEDULE_TAB) }),
    component: 'postDay',
    isShow: true,
  },
  {
    id: EHANDOVER_TAB_IDS.MY_DRAFT_TAB,
    flag: false,
    title: getTabTitleWithoutCount(
      i18n.t('EHANDOVER_MY_DRAFT_TAB', { count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.MY_DRAFT_TAB) })
    ),
    component: 'myDraft',
    isShow: true,
  },
  {
    id: EHANDOVER_TAB_IDS.DELETED_TAB,
    flag: false,
    title: i18n.t('EHANDOVER_TAB_DELETED', { count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.DELETED_TAB) }),
    component: 'deleted',
    isShow: true,
  },
  {
    id: EHANDOVER_TAB_IDS.NOTIFICATION_TAB,
    flag: false,
    title: i18n.t('EHANDOVER_NOTIFICATION', {
      count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.NOTIFICATION_TAB),
    }),
    component: 'notification',
    isShow: true,
  },
  {
    id: EHANDOVER_TAB_IDS.REQUIRE_ACK_TAB,
    flag: false,
    title: i18n.t('EHANDOVER_REQUIRE_ACK_TAB', {
      count: getTabCount(tabCountsById, EHANDOVER_TAB_IDS.REQUIRE_ACK_TAB),
    }),
    component: 'requireAck',
    isShow: true,
  },
];

// 根据页面当前已有 total，生成每个 tab 最终展示的标题。
// 这里只负责“把 total 映射成文案”，不参与任何 total 请求和刷新时机判断。
export const getTabDynamicTitle = ({ tabId, originalTitle, i18n, myDraftLoaded, tabCountsById }) => {
  const titleConfig = {
    [EHANDOVER_TAB_IDS.PIN_TAB]: { key: 'EHANDOVER_PIN_TAB' },
    [EHANDOVER_TAB_IDS.SENT_TAB]: { key: 'EHANDOVER_ALL' },
    [EHANDOVER_TAB_IDS.CHECKLIST_TAB]: { key: 'EHANDOVER_FOLLOWUP' },
    [EHANDOVER_TAB_IDS.OUTSTANDING_TAB]: { key: 'EHANDOVER_OUTSTANDING_CHECKLIST_TAB' },
    [EHANDOVER_TAB_IDS.COMPLETED_TAB]: { key: 'EHANDOVER_COMPLETED_CHECKLIST_TAB' },
    [EHANDOVER_TAB_IDS.SCHEDULE_TAB]: { key: 'EHANDOVER_TAB_POSTDAY' },
    [EHANDOVER_TAB_IDS.MY_DRAFT_TAB]: {
      key: 'EHANDOVER_MY_DRAFT_TAB',
      shouldShowCount: myDraftLoaded,
    },
    [EHANDOVER_TAB_IDS.DELETED_TAB]: { key: 'EHANDOVER_TAB_DELETED' },
    [EHANDOVER_TAB_IDS.NOTIFICATION_TAB]: { key: 'EHANDOVER_NOTIFICATION' },
    [EHANDOVER_TAB_IDS.REQUIRE_ACK_TAB]: { key: 'EHANDOVER_REQUIRE_ACK_TAB' },
  };

  const config = titleConfig[tabId];
  if (config) {
    const count = getTabCount(tabCountsById, tabId);

    // MY_DRAFT_TAB 在未真正进入 tab 之前不显示 count，所以这里要保留它的特殊分支。
    if (config.shouldShowCount === false) {
      return getTabTitleWithoutCount(i18n.t(config.key, { count }));
    }

    return i18n.t(config.key, { count });
  }

  return originalTitle;
};
